import React, { useState } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Main } from '../../../layouts';
import Container from '../../../components/Container';
import GamificationTab from '../../../components/vanliferComponents/Community/Profile/GamificationTab';
import { useAppSelector } from '../../../hooks/hooks';
import { useParams } from 'react-router-dom';
import AchievementsSection from '../../../components/vanliferComponents/Community/Profile/AchievementsSection';
import DiscoverTab from '../../../components/vanliferComponents/Community/Profile/DiscoverTab';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import ExploreRoundedIcon from '@mui/icons-material/ExploreRounded';

export default function ProfilePage() {
    const theme = useTheme();
    const mode = theme.palette.mode;

    const communityName = useAppSelector((state) => state.user.user.communityName);
    const { communityName: communityNameFromParams } = useParams();
    const profileCommunityName = communityNameFromParams ?? communityName;
    const isOwner = profileCommunityName === communityName;

    const [activeTab, setActiveTab] = useState(0);

    const tabs = [
        {
            label: 'Über mich',
            subtitle: isOwner ? 'Deine Community-Statistiken' : 'Profil- und Reisekennzahlen',
            icon: <AccountCircleRoundedIcon />,
            content: <AchievementsSection communityName={profileCommunityName!} isOwner={isOwner} />,
        },
        {
            label: 'Erfolge',
            subtitle: isOwner ? 'Badges, Rang und Verlauf' : 'Rang, Badges und Community-Highlights',
            icon: <EmojiEventsRoundedIcon />,
            content: <GamificationTab communityName={profileCommunityName!} isOwner={isOwner} />,
        },
        {
            label: 'Entdecken',
            subtitle: 'Vanlifer entdecken',
            icon: <ExploreRoundedIcon />,
            content: <DiscoverTab />,
        },
    ];

    return (
        <Main>
            <Container>
                <Typography
                    sx={{
                        textTransform: 'uppercase',
                        fontWeight: 500,
                        letterSpacing: 1,
                    }}
                    gutterBottom
                    color="text.secondary"
                >
                    Community
                </Typography>
                <Box mb={2}>
                    <Typography
                        variant="h3"
                        component="h2"
                        color="text.primary"
                        fontWeight={700}
                        lineHeight={1.2}
                    >
                        {isOwner ? (
                            <>
                                Mein öffentliches{' '}
                                <Typography
                                    component="span"
                                    variant="inherit"
                                    color="text.primary"
                                    sx={{
                                        background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                            mode === 'light'
                                                ? theme.palette.secondary.main
                                                : theme.palette.primary.main,
                                            0.3
                                        )} 0%)`,
                                    }}
                                >
                                    Profil
                                </Typography>
                            </>
                        ) : (
                            <>
                                Profil von{' '}
                                <Typography
                                    component="span"
                                    variant="inherit"
                                    color="text.primary"
                                    sx={{
                                        background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                            mode === 'light'
                                                ? theme.palette.secondary.main
                                                : theme.palette.primary.main,
                                            0.3
                                        )} 0%)`,
                                    }}
                                >
                                    {profileCommunityName}
                                </Typography>
                            </>
                        )}
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            height: 'fit-content',
                            border: 0,
                            boxShadow: 'none',
                            bgcolor: 'background.paper',
                        }}
                    >
                        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {tabs.map((tab, index) => {
                                const active = activeTab === index;
                                return (
                                    <ListItem key={tab.label} disablePadding>
                                    <ListItemButton
                                        onClick={() => setActiveTab(index)}
                                        sx={{
                                            borderRadius: 2,
                                            px: 1.25,
                                            py: 1,
                                            gap: 0.5,
                                            ...(active
                                                ? {
                                                    bgcolor: 'action.selected',
                                                    '&:hover': { bgcolor: 'action.selected' },
                                                }
                                                : {}),
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>
                                            {tab.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Box sx={{ fontWeight: 800, color: 'text.primary' }}>{tab.label}</Box>}
                                            secondary={tab.subtitle}
                                        />
                                    </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            border: 0,
                            boxShadow: 'none',
                            bgcolor: 'background.paper',
                        }}
                    >
                        {tabs.map((tab, index) => (
                            <Box
                                key={tab.label}
                                role="tabpanel"
                                hidden={activeTab !== index}
                                sx={{ display: activeTab === index ? 'block' : 'none' }}
                            >
                                {tab.content}
                            </Box>
                        ))}
                    </Paper>
                </Box>
            </Container>
        </Main>
    );
}
